import {
  Box,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormLabel,
  Grid,
  IconButton,
  MenuItem,
  TextField,
  Typography,
} from "@mui/material";
import { Close, Delete, Edit } from "@mui/icons-material";
import React, { useEffect, useState } from "react";
import { collection, deleteDoc, doc, getDocs, setDoc, updateDoc } from "firebase/firestore";
import { getDownloadURL, ref, uploadBytes } from "firebase/storage";
import { firestore, storage } from "../../..";
import { useFeedback } from "../../../providers/feedback/feedback";
import { defaultEditorialBoard, EditorialCommitteeMember, sortEditorialCommitteeMembers } from "../../yuva_gaaz/yuvagaaz_data";
import { toTitleCaseName } from "../../../utils/title_case";

const EMPTY_FORM = { name: "", post: "Member", place: "", img: "" };
const POST_OPTIONS = ["Director", "Editor", "Co-Editor", "Member"];

function normalizeUrl(value: string) {
  const trimmedValue = value.trim();
  if (!trimmedValue) return "";
  if (trimmedValue.startsWith("http://") || trimmedValue.startsWith("https://") || trimmedValue.startsWith("/")) {
    return trimmedValue;
  }
  return `https://${trimmedValue}`;
}

export default function ManageEditorialMembersTab() {
  const { showSnackbar } = useFeedback();
  const [members, setMembers] = useState<EditorialCommitteeMember[]>([]);
  const [editingMember, setEditingMember] = useState<EditorialCommitteeMember | null>(null);
  const [formValues, setFormValues] = useState(EMPTY_FORM);
  const [selectedImage, setSelectedImage] = useState<File | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  async function seedDefaultMembers() {
    const timestamp = new Date().toISOString();
    await Promise.all(
      defaultEditorialBoard.map((member) =>
        setDoc(doc(firestore, "yuvagaaz_editorial_committee", `${member.post}-${member.name}`.replace(/[^a-zA-Z0-9]+/g, "-").toLowerCase()), {
          ...member,
          createdAt: timestamp,
          updatedAt: timestamp,
        }),
      ),
    );
  }

  async function fetchMembers() {
    try {
      setIsLoading(true);
      const snapshot = await getDocs(collection(firestore, "yuvagaaz_editorial_committee"));
      if (!snapshot.docs.length) {
        await seedDefaultMembers();
        const seededSnapshot = await getDocs(collection(firestore, "yuvagaaz_editorial_committee"));
        setMembers(sortEditorialCommitteeMembers(seededSnapshot.docs.map((memberDoc) => ({ docId: memberDoc.id, ...(memberDoc.data() as Omit<EditorialCommitteeMember, "docId">) }))));
        return;
      }
      setMembers(sortEditorialCommitteeMembers(snapshot.docs.map((memberDoc) => ({ docId: memberDoc.id, ...(memberDoc.data() as Omit<EditorialCommitteeMember, "docId">) }))));
    } catch (error) {
      console.error("Failed to load committee members", error);
      showSnackbar("Failed to load committee members");
    } finally {
      setIsLoading(false);
    }
  }

  useEffect(() => {
    fetchMembers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function handleEdit(member: EditorialCommitteeMember) {
    setEditingMember(member);
    setFormValues({ name: member.name, post: member.post, place: member.place || "", img: member.img || "" });
    setSelectedImage(null);
  }

  async function handleUpdate() {
    if (!editingMember?.docId) return;
    try {
      let finalImage = formValues.img.trim();
      if (selectedImage) {
        const storageRef = ref(storage, `yuvagaaz/editorial-committee/${Date.now()}-${selectedImage.name}`);
        await uploadBytes(storageRef, selectedImage);
        finalImage = await getDownloadURL(storageRef);
      }

      await updateDoc(doc(firestore, "yuvagaaz_editorial_committee", editingMember.docId), {
        name: toTitleCaseName(formValues.name),
        post: formValues.post.trim(),
        place: formValues.place.trim(),
        img: normalizeUrl(finalImage),
        updatedAt: new Date().toISOString(),
      });

      showSnackbar("Committee member updated successfully");
      setEditingMember(null);
      setFormValues(EMPTY_FORM);
      setSelectedImage(null);
      fetchMembers();
    } catch (error) {
      console.error("Failed to update committee member", error);
      showSnackbar("Failed to update committee member");
    }
  }

  async function handleDelete(member: EditorialCommitteeMember) {
    if (!member.docId) return;

    if (!window.confirm(`Delete "${member.name}"?`)) {
      return;
    }

    try {
      await deleteDoc(doc(firestore, "yuvagaaz_editorial_committee", member.docId));
      showSnackbar("Committee member deleted successfully");
      fetchMembers();
    } catch (error) {
      console.error("Failed to delete committee member", error);
      showSnackbar("Failed to delete committee member");
    }
  }

  if (isLoading) {
    return <Box py={6} textAlign="center"><Typography>Loading committee members...</Typography></Box>;
  }

  return (
    <Box mt={3}>
      <Grid container spacing={3}>
        {members.map((member) => (
          <Grid item xs={12} sm={6} md={4} key={member.docId || `${member.name}-${member.post}`}>
            <Card sx={{ height: "100%" }}>
              {member.img ? <Box component="img" src={member.img} alt={member.name} sx={{ width: "100%", height: 240, objectFit: "cover", objectPosition: "center top" }} /> : null}
              <CardContent>
                <Typography variant="h6" fontWeight={700}>{member.name}</Typography>
                <Typography variant="body2" color="text.secondary">{member.post}</Typography>
                {member.place ? <Typography variant="body2" color="text.secondary">{member.place}</Typography> : null}
                <Box display="flex" gap={1} mt={2} flexWrap="wrap">
                  <Button variant="outlined" startIcon={<Edit />} onClick={() => handleEdit(member)}>Edit</Button>
                  <Button color="error" variant="outlined" startIcon={<Delete />} onClick={() => handleDelete(member)}>Delete</Button>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      <Dialog open={Boolean(editingMember)} onClose={() => setEditingMember(null)} fullWidth maxWidth="sm">
        <DialogTitle>Edit Committee Member</DialogTitle>
        <DialogContent>
          <Box mt={1} display="flex" flexDirection="column" gap={2}>
            <TextField
              fullWidth
              label="Member Name"
              value={formValues.name}
              onChange={(event) =>
                setFormValues((prev) => ({ ...prev, name: toTitleCaseName(event.target.value) }))
              }
            />
            <TextField select fullWidth label="Role" value={formValues.post} onChange={(event) => setFormValues((prev) => ({ ...prev, post: event.target.value }))}>
              {POST_OPTIONS.map((option) => <MenuItem key={option} value={option}>{option}</MenuItem>)}
            </TextField>
            <TextField fullWidth label="Place" value={formValues.place} onChange={(event) => setFormValues((prev) => ({ ...prev, place: event.target.value }))} />
            <TextField fullWidth label="Image URL" value={formValues.img} onChange={(event) => { setFormValues((prev) => ({ ...prev, img: event.target.value })); if (event.target.value) setSelectedImage(null); }} />
            <FormControl fullWidth>
              <FormLabel>Upload New Member Image</FormLabel>
              <input type="file" accept="image/*" style={{ border: "1px solid rgb(181, 176, 176)", width: "96%", paddingLeft: 8, paddingRight: 8, paddingTop: 16, paddingBottom: 16, borderRadius: 5, marginTop: 8 }} onChange={(event) => { if (event.target.files?.[0]) { setSelectedImage(event.target.files[0]); setFormValues((prev) => ({ ...prev, img: "" })); } }} />
              {selectedImage ? (
                <Box mt={2} position="relative" display="inline-block">
                  <img src={URL.createObjectURL(selectedImage)} alt="Updated committee member preview" style={{ maxWidth: "220px", maxHeight: "220px", borderRadius: 8 }} />
                  <IconButton size="small" onClick={() => setSelectedImage(null)} style={{ position: "absolute", top: 0, right: 0, color: "#ffffff", backgroundColor: "rgba(0,0,0,0.5)" }}>
                    <Close />
                  </IconButton>
                </Box>
              ) : null}
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditingMember(null)} variant="outlined">Cancel</Button>
          <Button onClick={handleUpdate} variant="contained">Update</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
